import { useMemo, useRef } from "react";
import { SchemaOf, ValidationError } from "yup";
import { KeyObj } from "../types/keys/key-object";
import { keySet } from "../types/keys/key-set";
import { StringDict } from "../types/keys/type-key-of";

export const getYupValidationForObject = <Obj extends object>(
  obj: Obj,
  schema: SchemaOf<Obj>
) => {
  let validationMsg: Partial<KeyObj<Obj, string>> = {};

  try {
    schema.validateSync(obj, {
      abortEarly: false,
    });
  } catch (e) {
    const err = e as ValidationError;
    err.inner?.forEach((error) => {
      validationMsg[error.path] = error.message;
    });
  }
  return validationMsg;
};

export const getYupValidationMessages = <Obj extends object>(
  obj: Obj,
  yupSchema: SchemaOf<Obj>
): Partial<StringDict<Obj>> => {
  let validationMsg: Partial<KeyObj<Obj, string>> = {};

  for (let key of Object.keys(obj)) {
    try {
      // TODO: Do we want to get all errors to show to user? If so, abortEarly: false
      // TODO: Async
      yupSchema?.validateSyncAt(key, obj, {
        context: obj,
        abortEarly: false,
      });
    } catch (e) {
      const validationError = e as ValidationError;

      if (validationError) {
        validationMsg[key] = validationError.message;
      }

      // AFAIK 'validateSyncAt()' can only possibly throw a 'ValidationError' when properly used, so any other errors should not be caught
      else throw e;
    }
  }
  return validationMsg;
};

export function useValidateObject<Obj extends Record<string, any>>(
  obj: Obj,
  yupSchema?: SchemaOf<Obj>
) {
  const keys = useRef(keySet(obj));

  const validationMsg = useMemo(() => {
    if (!yupSchema) return {} as Partial<StringDict<Obj>>;
    return getYupValidationMessages(obj, yupSchema);
  }, [obj, yupSchema]);

  const isInvalid = useMemo(() => {
    let isInvalid: Partial<KeyObj<Obj, boolean>> = {};

    for (let key of keys.current) {
      isInvalid[key] = validationMsg?.[key] !== undefined;
    }

    return isInvalid as KeyObj<Obj, boolean>;
  }, [validationMsg]);

  const hasAllValid: boolean = useMemo(() => {
    for (let key of keys.current) {
      if (isInvalid[key]) return false;
    }
    return true;
  }, [isInvalid]);

  return {
    validationMsg,
    isInvalid,
    hasAllValid,
  };
}

export const getArrayYupValidationMessages = <T extends object>(
  values: T[],
  yupSchema: SchemaOf<T>
) => {
  return values.map((value) => {
    const messages = getYupValidationMessages(value, yupSchema);
    return messages;
  });
};

export const useValidateArray = <T extends object>(
  values: T[],
  yupSchema: SchemaOf<T>
) => {
  const validationMsg = useMemo(() => {
    return getArrayYupValidationMessages(values, yupSchema);
  }, [values, yupSchema]);

  const isInvalid: KeyObj<T, boolean>[] = useMemo(() => {
    return validationMsg.map((msg) => {
      let isInvalid: Partial<KeyObj<T, boolean>> = {};

      for (let key of Object.keys(msg)) {
        isInvalid[key] = msg[key] !== undefined;
      }

      return isInvalid as KeyObj<T, boolean>;
    });
  }, [validationMsg]);

  const hasAllValid: boolean = useMemo(() => {
    for (let msg of validationMsg) {
      for (let key of Object.keys(msg)) {
        if (msg[key] !== undefined) return false;
      }
    }
    return true;
  }, [validationMsg]);

  return {
    validationMsg,
    isInvalid,
    hasAllValid,
  };
};
